import SearchIcon from '@mui/icons-material/Search';
import { InputBase, useTheme } from '@mui/material';
import React from 'react';

import type { FilterBarSearchConfig } from './FilterBar.props';
import { controlEngagedSx } from './filterBarStyles';

export interface FilterBarSearchBoxProps {
  config: FilterBarSearchConfig;
  value: string;
  onChange: (value: string) => void;
  inputSx: Record<string, unknown>;
}

const DEFAULT_PLACEHOLDER = 'Search all fields...';
const SEARCH_BOX_WIDTH = 240;

/**
 * The free-text search input.
 *
 * `placement` decides both position and who claims the right-hand edge: a
 * trailing box takes `ml: 'auto'` itself, a leading box sits first in the strip
 * and leaves that to the Search button.
 */
export const FilterBarSearchBox: React.FC<FilterBarSearchBoxProps> = ({
  config,
  value,
  onChange,
  inputSx,
}) => {
  const theme = useTheme();
  const placement = config.placement ?? 'trailing';
  if (placement === 'none') return null;

  const placeholder = config.placeholder ?? DEFAULT_PLACEHOLDER;
  // A leading box reads as a search field rather than a filter, so it carries
  // the magnifier by default — matching what Users and Clients render today.
  const withAdornment = config.adornment ?? placement === 'leading';

  return (
    <InputBase
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      inputProps={{ 'aria-label': config.ariaLabel ?? placeholder }}
      startAdornment={
        withAdornment ? (
          <SearchIcon
            sx={{ fontSize: 15, mr: 0.5, color: theme.palette.v4.textFaint }}
            aria-hidden
          />
        ) : undefined
      }
      sx={{
        ...inputSx,
        // Typed-in text engages the box, matching the quick slots beside it.
        ...(value ? controlEngagedSx(theme) : {}),
        width: SEARCH_BOX_WIDTH,
        ...(placement === 'trailing' ? { ml: 'auto' } : {}),
      }}
    />
  );
};
